// ============================================================================
//  /simulacion — alta de escenario sin hardware (trabajadores con su fotocheck
//  vinculado y las cadenas de lectores de cada bocamina) más el MOTOR que hace
//  caminar a esa gente. Solo escribe por la API (ver modules/simulation); no
//  habla MQTT ni escucha sockets: quien publica es el servidor.
//
//  El efecto de la simulación NO se ve en esta pantalla — se ve en el tablero
//  y en /pruebas. Acá solo está el interruptor y el recuento por fase, que es
//  lo único que el motor sabe de sí mismo.
// ============================================================================
import { esc, getJson, toast } from '../shared/api';
import type {
  SimulationCompany,
  SimulationEngineConfig,
  SimulationEngineStatus,
  SimulationEngineStopResult,
  SimulatedPortal,
  SimulatedScannersResult,
  SimulatedWorker,
  SimulatedWorkersResult,
  SimulationStatus,
} from '../../src/shared/types';

function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

function val(id: string): string {
  return ($(id) as HTMLInputElement | HTMLSelectElement | null)?.value ?? '';
}

function onClick(id: string, fn: () => void | Promise<void>): void {
  $(id)?.addEventListener('click', () => void fn());
}

/** Como postJson/delJson de shared/api, pero devolviendo el cuerpo: estas
 *  respuestas traen el resumen de lo hecho ("15 nuevos, 10 ya existían") y un
 *  toast de "Guardado" no diría nada. El error sigue saliendo igual: el
 *  mensaje en español que manda el servidor en `error`. */
async function send<T>(url: string, method: 'POST' | 'DELETE', body?: unknown): Promise<T | null> {
  try {
    const res = await fetch(url, {
      method,
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as T & { error?: string };
    if (res.ok) return data;
    toast(data.error || `error ${res.status}`, true);
    return null;
  } catch {
    toast('Sin conexión con el servidor', true);
    return null;
  }
}

let status: SimulationStatus | null = null;

function renderWorkers(workers: SimulatedWorker[]): void {
  const body = $('sm-workers');
  const empty = $('sm-workers-empty');
  const count = $('sm-w-count');
  if (!body || !empty || !count) return;
  count.textContent = workers.length ? `${workers.length} activos` : '';
  empty.hidden = workers.length > 0;
  body.innerHTML = workers
    .map(
      (w) => `<tr>
        <td class="mono">${esc(w.dni)}</td>
        <td>${esc(w.name)}</td>
        <td>${esc(w.companyName)}</td>
        <td class="mono">${esc(w.badgeMac) || '<span class="muted">sin fotocheck</span>'}</td>
        <td class="mono">${esc(w.badgeCode)}</td>
      </tr>`,
    )
    .join('');
}

function renderPortals(portals: SimulatedPortal[]): void {
  const body = $('sm-portals');
  const empty = $('sm-portals-empty');
  const all = $('sm-scanners-all') as HTMLButtonElement | null;
  if (!body || !empty) return;
  empty.hidden = portals.length > 0;
  if (all) all.disabled = !portals.some((p) => p.canSimulate);
  body.innerHTML = portals
    .map((p) => {
      const chain = p.scannerCount
        ? `${p.scannerCount} lector${p.scannerCount === 1 ? '' : 'es'}` +
          (p.simulatedCount ? ` <span class="muted">(${p.simulatedCount} simulados)</span>` : '')
        : '<span class="muted">sin lectores</span>';
      const action = p.canSimulate
        ? `<button class="btn btn-save btn-sm" data-create="${p.id}" type="button">Crear cadena de 3</button>`
        : `<span class="muted">${esc(p.reason)}</span>`;
      const remove = p.simulatedCount
        ? ` <button class="btn btn-ghost btn-sm" data-remove="${p.id}" type="button">Quitar simulados</button>`
        : '';
      return `<tr>
        <td>${esc(p.name)}</td>
        <td class="mono">${esc(p.topicBase)}</td>
        <td>${chain}</td>
        <td class="right">${action}${remove}</td>
      </tr>`;
    })
    .join('');
}

/** Las empresas registradas (`companies`). Sin ninguna cargada se ofrece la de
 *  por defecto: el alta exige empresa, así que un selector vacío dejaría la
 *  pantalla muerta en una instalación nueva — y no hay pantalla de empresas en
 *  este repo para ir a crear una. */
function renderCompanies(companies: SimulationCompany[], fallback: string): void {
  const select = $('sm-company') as HTMLSelectElement | null;
  const help = $('sm-company-help');
  if (!select) return;
  const chosen = select.value;
  const names = companies.length ? companies.map((c) => c.name) : [fallback];
  select.innerHTML = names.map((n) => `<option value="${esc(n)}">${esc(n)}</option>`).join('');
  // conservar lo elegido entre recargas del estado (hay una tras cada alta)
  if (chosen && names.includes(chosen)) select.value = chosen;
  if (help) {
    help.textContent = companies.length
      ? ''
      : `No hay empresas registradas todavía: se usa «${fallback}».`;
  }
}

function render(): void {
  if (!status) return;
  renderWorkers(status.workers);
  renderPortals(status.portals);
  renderCompanies(status.companies, status.defaultCompany);
  const count = $('sm-count') as HTMLInputElement | null;
  if (count) count.max = String(status.maxWorkers);
}

async function load(): Promise<void> {
  try {
    status = await getJson<SimulationStatus>('/api/simulation');
    render();
  } catch {
    toast('No se pudo cargar el estado de la simulación', true);
  }
}

onClick('sm-create', async () => {
  const count = Number(val('sm-count'));
  if (!Number.isInteger(count) || count <= 0) {
    toast('Indicá una cantidad entera mayor que cero', true);
    return;
  }
  const res = await send<SimulatedWorkersResult>('/api/simulation/workers', 'POST', {
    count,
    companyName: val('sm-company').trim(),
  });
  if (!res) return;
  toast(
    res.created
      ? `${res.created} trabajador${res.created === 1 ? '' : 'es'} creado${res.created === 1 ? '' : 's'}` +
          (res.existing ? `, ${res.existing} ya existían` : '')
      : 'Ya estaban todos creados',
  );
  void load();
});

onClick('sm-deactivate', async () => {
  const res = await send<{ deactivated: number }>('/api/simulation/workers', 'DELETE');
  if (!res) return;
  toast(
    res.deactivated
      ? `${res.deactivated} dados de baja`
      : 'No había trabajadores simulados activos',
  );
  void load();
});

function reportScanners(res: SimulatedScannersResult): void {
  const created = res.portals.reduce((acc, p) => acc + p.created, 0);
  const skipped = res.portals.filter((p) => p.skipped);
  if (created) {
    toast(
      `${created} lectores creados` +
        (skipped.length ? `; ${skipped.length} bocamina(s) sin tocar` : ''),
    );
  } else {
    // el motivo del servidor ya está en español y es lo único útil acá
    toast(skipped[0]?.reason || 'No había nada que crear', true);
  }
  void load();
}

onClick('sm-scanners-all', async () => {
  const res = await send<SimulatedScannersResult>('/api/simulation/scanners', 'POST', {});
  if (res) reportScanners(res);
});

$('sm-portals')?.addEventListener('click', (ev) => {
  const el = (ev.target as HTMLElement).closest('button');
  if (!el) return;
  const create = el.getAttribute('data-create');
  const remove = el.getAttribute('data-remove');
  if (create) {
    void send<SimulatedScannersResult>('/api/simulation/scanners', 'POST', {
      portalId: Number(create),
    }).then((res) => {
      if (res) reportScanners(res);
    });
    return;
  }
  if (remove) {
    void send<{ deleted: number }>(
      `/api/simulation/scanners?portalId=${encodeURIComponent(remove)}`,
      'DELETE',
    ).then((res) => {
      if (!res) return;
      toast(res.deleted ? `${res.deleted} lectores simulados eliminados` : 'No había simulados');
      void load();
    });
  }
});

// ============================================================================
//  Motor de simulación — el interruptor. Todo lo de acá va a NIVEL DE MÓDULO:
//  registrado dentro de otro handler, los listeners solo existirían después de
//  apretar ese otro botón.
// ============================================================================
let engine: SimulationEngineStatus | null = null;
/** sondeo del motor mientras corre; se apaga al detenerlo para no dejar un
 *  fetch cada 2 s contra una pantalla que ya no muestra nada vivo */
let enginePoll: ReturnType<typeof setInterval> | null = null;

function num(id: string, fallback: number): number {
  const raw = val(id).trim();
  if (raw === '') return fallback;
  const n = Math.trunc(Number(raw));
  return Number.isFinite(n) ? n : fallback;
}

/** "30, 50, 70" -> [30, 50, 70]. Los rangos los valida el servidor y contesta
 *  en español; acá solo se parte el texto. */
function stayList(): number[] {
  return val('sm-eng-stay')
    .split(/[,\s]+/)
    .map((x) => Math.trunc(Number(x)))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function engineConfig(): Partial<SimulationEngineConfig> {
  return {
    workers: num('sm-eng-workers', 0),
    stayMinutes: stayList(),
    outsideMinutes: num('sm-eng-outside', 5),
    hopSeconds: num('sm-eng-hop', 20),
    arrivalSpreadMinutes: num('sm-eng-spread', 10),
    portalSwitchEvery: num('sm-eng-switch', 3),
  };
}

function fillEngineForm(cfg: SimulationEngineConfig): void {
  const set = (id: string, v: string): void => {
    const el = $(id) as HTMLInputElement | null;
    // no se pisa lo que el operador está tipeando con el motor andando
    if (el && document.activeElement !== el) el.value = v;
  };
  set('sm-eng-workers', String(cfg.workers));
  set('sm-eng-stay', cfg.stayMinutes.join(', '));
  set('sm-eng-outside', String(cfg.outsideMinutes));
  set('sm-eng-hop', String(cfg.hopSeconds));
  set('sm-eng-spread', String(cfg.arrivalSpreadMinutes));
  set('sm-eng-switch', String(cfg.portalSwitchEvery));
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds} s`;
  const m = Math.floor(seconds / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${m % 60} min`;
}

function syncEnginePoll(running: boolean): void {
  if (running && !enginePoll) {
    enginePoll = setInterval(() => void loadEngine(), 2000);
  } else if (!running && enginePoll) {
    clearInterval(enginePoll);
    enginePoll = null;
  }
}

function renderEngine(e: SimulationEngineStatus): void {
  engine = e;
  const state = $('sm-eng-state');
  if (state) {
    const seconds = e.startedAt ? Math.floor(Date.now() / 1000 - e.startedAt) : 0;
    state.textContent = e.running ? `corriendo · ${formatUptime(seconds)}` : 'detenido';
    state.className = e.running ? 'tp-count sm-run' : 'tp-count';
  }
  $('sm-eng-start')?.toggleAttribute('hidden', e.running);
  $('sm-eng-stop')?.toggleAttribute('hidden', !e.running);
  $('sm-eng-drain')?.toggleAttribute('hidden', !e.running);
  const startBtn = $('sm-eng-start') as HTMLButtonElement | null;
  if (startBtn) startBtn.disabled = !e.canStart && !e.running;

  const reason = $('sm-eng-reason');
  if (reason) {
    // el motivo ya viene redactado en español del servidor
    const text = e.running ? e.lastError : e.reason || e.lastError;
    reason.textContent = text;
    reason.toggleAttribute('hidden', !text);
  }

  const counters = $('sm-eng-counters');
  if (counters) {
    counters.textContent =
      e.running || e.published
        ? `${e.published} mensajes · ${e.entries} ingresos · ${e.exits} salidas`
        : '';
  }

  const body = $('sm-eng-portals');
  if (body) {
    body.innerHTML = e.portals
      .map(
        (p) =>
          `<tr><td>${esc(p.name)}</td><td class="right">${p.assigned}</td>` +
          `<td class="right">${p.outside}</td><td class="right">${p.entering}</td>` +
          `<td class="right">${p.inside}</td><td class="right">${p.exiting}</td></tr>`,
      )
      .join('');
  }
  $('sm-eng-empty')?.toggleAttribute('hidden', e.portals.length > 0);
  fillEngineForm(e.config);
  syncEnginePoll(e.running);
}

async function loadEngine(): Promise<void> {
  try {
    renderEngine(await getJson<SimulationEngineStatus>('/api/simulation/engine'));
  } catch {
    // el sondeo no molesta con un toast cada 2 s: el motivo aparece cuando el
    // operador vuelve a apretar algo
    syncEnginePoll(false);
  }
}

onClick('sm-eng-start', async () => {
  const res = await send<SimulationEngineStatus>('/api/simulation/engine', 'POST', engineConfig());
  if (!res) {
    // el rechazo ya salió por toast; se relee para que la banda de motivo
    // quede al día (p. ej. se cayó el broker desde la última carga)
    await loadEngine();
    return;
  }
  renderEngine(res);
  const dentro = res.portals.reduce((n, p) => n + p.inside, 0);
  toast(
    dentro
      ? `Simulación en marcha; se retomaron ${dentro} que ya estaban adentro`
      : 'Simulación en marcha',
  );
});

/** `drain` = además publicar la salida de todos. Sin él la gente queda adentro
 *  y el próximo arranque la retoma desde ahí, que es lo que se quiere de una
 *  prueba que se corta y se continúa. */
async function stopEngine(drain: boolean): Promise<void> {
  const res = await send<SimulationEngineStopResult>(
    `/api/simulation/engine${drain ? '?drain=true' : ''}`,
    'DELETE',
  );
  if (!res) return;
  toast(
    res.drained
      ? `Simulación detenida; ${res.drained} personas sacadas de la mina`
      : 'Simulación detenida; los que están adentro se retoman al reiniciar',
  );
  await loadEngine();
}

onClick('sm-eng-stop', () => stopEngine(false));
onClick('sm-eng-drain', () => stopEngine(true));

// aplicar la config en caliente sin detener: el POST reconfigura
$('sm-eng-portals')
  ?.closest('.card')
  ?.addEventListener('change', (ev) => {
    if (!engine?.running) return;
    if ((ev.target as HTMLElement).tagName !== 'INPUT') return;
    void send<SimulationEngineStatus>('/api/simulation/engine', 'POST', engineConfig()).then(
      (res) => {
        if (res) renderEngine(res);
      },
    );
  });

void load();
void loadEngine();
